import { useState } from 'react';
import specialCondenserService from '../../services/specialCondenserService';
import specialFloodedEvaporatorService from '../../services/specialFloodedEvaporatorService';
import coaxialCondenserService from '../../services/coaxialCondenserService';
import coaxialEvaporatorService from '../../services/coaxialEvaporatorService';


interface TDUConversionResult {
  data: any;
  error: string | null;
}

interface ServiceResponse {
  data: any;
  status: number;
  statusText?: string;
}

export const useTDUConversion = () => {
  const [isLoading, setIsLoading] = useState(false);

  const convertTDUFile = async (file: File, appName: string): Promise<TDUConversionResult> => {
    setIsLoading(true);
    try {
      const content = await file.text();
      const tduData = JSON.parse(content);

      // Validate TDU file structure
      if (!tduData.dataObject) {
        return {
          data: null,
          error: 'Invalid TDU file format: The file is missing required data structure'
        };
      }

      // Map the app name to the correct service
      let result: ServiceResponse;
      
      try {
        switch (appName) {
          case 'App.p.OutsideCondensation':
            result = await specialCondenserService.convertTDUFile(tduData);
            break;
          case 'App.p.PoolBoiling':
            result = await specialFloodedEvaporatorService.convertTDUFile(tduData);
            break;
          case 'App.HeatExchangerWKC':
            result = await coaxialCondenserService.convertTDUFile(tduData);
            break;
          case 'App.HeatExchangerWKE':
            result = await coaxialEvaporatorService.convertTDUFile(tduData);
            break;
          default:
            return {
              data: null,
              error: `Unsupported application type: ${appName}. Please select a valid application type.`
            };
        }
      } catch (serviceError) {
        return {
          data: null,
          error: `Conversion failed: ${serviceError instanceof Error ? serviceError.message : 'An unexpected error occurred during conversion'}`
        };
      }

      if (!result?.data) {
        return {
          data: null,
          error: 'Conversion failed: The service did not return any data. Please check your input file and try again.'
        };
      }

      if (result.status >= 400) {
        return {
          data: null,
          error: `Conversion failed: ${result.statusText || 'An error occurred during the conversion process'}`
        };
      }

      return {
        data: result.data,
        error: null
      };
    } catch (error) {
      if (error instanceof SyntaxError) {
        return {
          data: null,
          error: 'Invalid file format: The file is not a valid JSON file'
        };
      }
      
      if (error instanceof Error) {
        return {
          data: null,
          error: `File processing failed: ${error.message}`
        };
      }

      return {
        data: null,
        error: 'An unexpected error occurred while processing the file'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    convertTDUFile,
    isLoading
  };
}; 